<template>
    <v-container fluid class="container-full-bg-gray">
        <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
            {{ snackbar.text }}
        </v-snackbar>

        <app-row>
            <app-col cols="12">
                <v-card width="100%" class="rounded-lg">
                    <v-card-title class="d-flex">
                        <div class="mr-auto d-flex align-sm-center">
                            <v-icon v-if="entityType && entityType.ui_config && entityType.ui_config.icon" left>{{ entityType.ui_config.icon }}</v-icon>
                            <h4 class="primary_blue--text">{{ displayName }}</h4>
                        </div>
                        <v-spacer></v-spacer>
                        <div>
                            <v-btn
                                v-if="hasFilterTemplate"
                                icon
                                small
                                @click="showFilters = !showFilters"
                                :color="showFilters ? 'primary' : ''"
                            >
                                <v-icon>mdi-filter-variant</v-icon>
                            </v-btn>
                            <v-btn icon color="primary" @click="loadInstances" :loading="loading">
                                <v-icon>mdi-refresh</v-icon>
                            </v-btn>
                            <v-btn v-if="!hideCreateButton" icon color="primary" @click="openCreateDialog" :disabled="loading">
                                <v-icon>mdi-plus</v-icon>
                            </v-btn>
                            <v-tooltip v-for="la in listActions" :key="la.code" bottom>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn
                                        icon
                                        :color="hasSelectedInstances ? 'primary' : 'grey'"
                                        :disabled="!hasSelectedInstances || executingListAction"
                                        :loading="executingListAction === la.code"
                                        v-bind="attrs"
                                        v-on="on"
                                        @click="executeListAction(la)"
                                    >
                                        <v-icon>{{ la.icon || 'mdi-play' }}</v-icon>
                                    </v-btn>
                                </template>
                                <span>{{ la.hover_text || la.description || la.code }}</span>
                            </v-tooltip>
                            <v-btn
                                v-if="showDeleteButton"
                                icon
                                :color="hasSelectedInstances ? 'error' : 'grey'"
                                @click="confirmDeleteSelected"
                                :disabled="!hasSelectedInstances"
                                :loading="deleting"
                            >
                                <v-icon>mdi-delete</v-icon>
                            </v-btn>
                        </div>
                    </v-card-title>
                    <v-expand-transition>
                        <div v-show="showFilters && hasFilterTemplate">
                            <v-divider></v-divider>
                            <v-card-text class="py-3 filter-section">
                                <DynamicComponentRenderer
                                    v-if="showFilters"
                                    :key="filterEditorKey"
                                    :schema="filterSchema"
                                    :code="entityType.ui_config.filter_template"
                                    :options="filterOptions"
                                    v-model="filterPayload"
                                    :actions_catalog="[]"
                                    :company_id="parseInt(companydb_id)"
                                    :delegateActions="['refresh_list']"
                                    @run_action="handleFilterAction"
                                />
                            </v-card-text>
                        </div>
                    </v-expand-transition>
                    <v-divider></v-divider>
                    <v-data-table
                        :headers="tableHeaders"
                        :items="instances"
                        :loading="loading"
                        :server-items-length="totalCount"
                        :options="tableOptions"
                        @update:options="onTableOptionsUpdate"
                        :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
                        item-key="__row_id__"
                        class="clickable-rows sticky-header-table"
                        @click:row="openEditDialog"
                    >
                        <template v-if="showSelectable" v-slot:header._select>
                            <v-checkbox
                                :input-value="allVisibleSelected"
                                :indeterminate="someVisibleSelected && !allVisibleSelected"
                                hide-details
                                class="ma-0 pa-0"
                                @change="toggleSelectAll"
                            />
                        </template>
                        <template v-slot:item="{ item }">
                            <tr class="clickable-row" @click="openEditDialog(item)">
                                <td v-if="showSelectable" class="text-center" style="width: 48px;" @click.stop>
                                    <v-checkbox
                                        :input-value="isSelected(item)"
                                        hide-details
                                        class="ma-0 pa-0"
                                        @click.stop="toggleSelection(item)"
                                    />
                                </td>
                                <td v-for="header in dataHeaders" :key="header.value">
                                    {{ getNestedValue(item, header.value) }}
                                </td>
                            </tr>
                        </template>
                        <template v-slot:no-data>
                            <div class="text-center pa-4 grey--text">No hay instancias</div>
                        </template>
                    </v-data-table>
                </v-card>
            </app-col>
        </app-row>

        <!-- Delete Confirmation Dialog -->
        <v-dialog v-model="deleteConfirmDialog" max-width="400" persistent>
            <v-card>
                <v-card-title class="error white--text py-3">
                    <v-icon dark left>mdi-alert</v-icon>
                    Confirmar eliminación
                </v-card-title>
                <v-card-text class="pt-4">
                    <span v-if="selectedCount === 1">
                        ¿Estás seguro de que deseas eliminar este registro?
                    </span>
                    <span v-else>
                        ¿Estás seguro de que deseas eliminar <strong>{{ selectedCount }}</strong> registros?
                    </span>
                    Esta acción no se puede deshacer.
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="cancelDelete">Cancelar</v-btn>
                    <v-btn color="error" @click="executeDelete" :loading="deleting">Eliminar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Create/Edit Dialog -->
        <v-dialog v-model="dialog" :max-width="dialogMaxWidth" @click:outside="handleClickOutside">
            <v-card :key="editorKey" class="dialog-card">
                <v-card-title v-if="!hideAllDialogButtons" :class="editingInstance ? 'primary white--text py-2' : 'secondary white--text py-2'" class="dialog-header">
                    <v-spacer></v-spacer>
                    <v-btn v-if="!hideDefaultClose" icon dark small @click="closeDialog">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                    <v-btn v-if="!hideDefaultSave" icon dark small @click="saveInstance" :loading="saving">
                        <v-icon>mdi-content-save</v-icon>
                    </v-btn>
                </v-card-title>
                <v-card-text class="pt-4 dialog-content">
                    <DynamicComponentRenderer
                        v-if="shouldUseCustomTemplate && entityType"
                        :key="editorKey"
                        :schema="entityType.schema"
                        :code="activeTemplate"
                        :options="activeOptions"
                        v-model="formPayload"
                        :actions_catalog="actionsCatalog"
                        :company_id="parseInt(companydb_id)"
                        :delegateActions="['save', 'close']"
                        @run_action="handleEditorAction"
                    />
                    <RecursivePayloadOnSchemaEditor
                        v-else-if="entityType && entityType.schema"
                        :key="editorKey"
                        :schema="entityType.schema"
                        v-model="formPayload"
                    />
                </v-card-text>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import AppRow from "@/components/layout/AppRow.vue";
import AppCol from "@/components/layout/AppCol.vue";
import RecursivePayloadOnSchemaEditor from "@/modules/entities/infra/RecursivePayloadOnSchemaEditor.vue";
import DynamicComponentRenderer from "@/modules/entities/infra/DynamicComponentRenderer.vue";
import { document_components_factory, async_command_dispatcher } from "@/api/client";

export default {
    name: "EntityInstancesView",
    components: {
        AppRow,
        AppCol,
        RecursivePayloadOnSchemaEditor,
        DynamicComponentRenderer
    },
    props: {
        companydb_id: {
            type: [Number, String],
            required: true
        },
        entity_type_uuid: {
            type: String,
            required: true
        }
    },
    data() {
        const component = document_components_factory.get_or_create_entity_instances_view_component(this.entity_type_uuid);
        return {
            screen_data: component.screen_data,
            component: component
        };
    },
    computed: {
        // Expose screen_data properties for template binding
        entityType() {
            return this.screen_data.entityType;
        },
        instances() {
            return this.screen_data.instances;
        },
        loading() {
            return this.screen_data.loading;
        },
        saving() {
            return this.screen_data.saving;
        },
        dialog: {
            get() {
                return this.screen_data.dialog;
            },
            set(value) {
                this.screen_data.dialog = value;
            }
        },
        editingInstance() {
            return this.screen_data.editingInstance;
        },
        formPayload: {
            get() {
                return this.screen_data.formPayload;
            },
            set(value) {
                this.screen_data.formPayload = value;
            }
        },
        filterPayload: {
            get() {
                return this.screen_data.filterPayload;
            },
            set(value) {
                this.screen_data.filterPayload = value;
            }
        },
        showFilters: {
            get() {
                return this.screen_data.showFilters;
            },
            set(value) {
                this.screen_data.showFilters = value;
            }
        },
        totalCount() {
            return this.screen_data.totalCount;
        },
        tableOptions() {
            return this.screen_data.tableOptions;
        },
        snackbar: {
            get() {
                return this.screen_data.snackbar;
            },
            set(value) {
                this.screen_data.snackbar = value;
            }
        },
        initialLoadDone() {
            return this.screen_data.initialLoadDone;
        },
        displayName() {
            if (!this.entityType) return 'Instances';
            if (this.entityType.ui_config) {
                const locale = this.$i18n ? this.$i18n.locale : 'es';
                const localeMap = { 'cat': 'display_name_cat', 'en': 'display_name_en', 'es': 'display_name_es' };
                const displayNameKey = localeMap[locale] || 'display_name_es';
                if (this.entityType.ui_config[displayNameKey]) {
                    return this.entityType.ui_config[displayNameKey];
                }
            }
            return this.entityType.name || 'Instances';
        },
        dialogMaxWidth() {
            if (this.entityType && this.entityType.ui_config && this.entityType.ui_config.dialog_size) {
                return this.entityType.ui_config.dialog_size;
            }
            return '600';
        },
        tableHeaders() {
            if (!this.entityType || !this.entityType.schema || !this.entityType.schema.properties) {
                return [];
            }
            let fields;
            if (this.entityType.ui_config && this.entityType.ui_config.header_fields && this.entityType.ui_config.header_fields.length > 0) {
                fields = this.entityType.ui_config.header_fields;
            } else {
                fields = Object.keys(this.entityType.schema.properties).slice(0, 5);
            }
            const headers = [];
            if (this.showSelectable) {
                headers.push({
                    text: '',
                    value: '_select',
                    sortable: false,
                    width: '48px'
                });
            }
            fields.forEach(key => {
                headers.push({
                    text: this.getNestedFieldTitle(key),
                    value: key,
                    sortable: true
                });
            });
            return headers;
        },
        dataHeaders() {
            return this.tableHeaders.filter(h => h.value !== '_select');
        },
        hasCustomTemplate() {
            return this.entityType && this.entityType.ui_config && (this.entityType.ui_config.editor_template || this.entityType.ui_config.create_template);
        },
        hasCreateTemplate() {
            return this.entityType && this.entityType.ui_config && this.entityType.ui_config.create_template;
        },
        activeTemplate() {
            if (!this.entityType || !this.entityType.ui_config) return null;
            if (!this.editingInstance && this.hasCreateTemplate) {
                return this.entityType.ui_config.create_template;
            }
            return this.entityType.ui_config.editor_template;
        },
        activeOptions() {
            if (!this.entityType || !this.entityType.ui_config) return '';
            if (!this.editingInstance && this.entityType.ui_config.create_options) {
                return this.entityType.ui_config.create_options;
            }
            return this.entityType.ui_config.editor_options || '';
        },
        shouldUseCustomTemplate() {
            return this.activeTemplate !== null;
        },
        hasFilterTemplate() {
            return this.entityType && this.entityType.ui_config && this.entityType.ui_config.filter_template;
        },
        filterSchema() {
            return {
                "type": "object",
                "properties": {
                    "filters": {
                        "type": "array",
                        "items": {
                            "type": "object",
                            "properties": {
                                "match_key": {"type": "string"},
                                "operator": {"type": "string"},
                                "value": {"type": "string"}
                            },
                            "required": ["match_key", "operator", "value"]
                        },
                        "title": "Filter criteria"
                    }
                },
                "required": ["filters"]
            };
        },
        filterOptions() {
            if (this.entityType && this.entityType.ui_config && this.entityType.ui_config.filter_options) {
                return this.entityType.ui_config.filter_options;
            }
            return '';
        },
        actionsCatalog() {
            if (this.entityType && this.entityType.actions_catalog) {
                return this.entityType.actions_catalog;
            }
            return [];
        },
        editorKey() {
            return this.screen_data.editorCounter;
        },
        filterEditorKey() {
            return this.screen_data.filterEditorCounter;
        },
        hideDefaultSave() {
            return this.entityType && this.entityType.ui_config && this.entityType.ui_config.hide_default_save === true;
        },
        hideDefaultClose() {
            return this.entityType && this.entityType.ui_config && this.entityType.ui_config.hide_default_close === true;
        },
        hideAllDialogButtons() {
            return this.hideDefaultSave && this.hideDefaultClose;
        },
        useNewPageMode() {
            return this.entityType && this.entityType.ui_config && this.entityType.ui_config.dialog_size === 'new_page';
        },
        hideCreateButton() {
            return this.entityType && this.entityType.ui_config && this.entityType.ui_config.hide_create_button === true;
        },
        showDeleteButton() {
            return this.entityType && this.entityType.ui_config && this.entityType.ui_config.show_delete_button === true;
        },
        listActions() {
            return this.actionsCatalog.filter(a => a.list_action === true);
        },
        showSelectable() {
            return this.showDeleteButton || this.listActions.length > 0;
        },
        executingListAction() {
            return this.screen_data.executingListAction || false;
        },
        deleting() {
            return this.screen_data.deleting;
        },
        deleteConfirmDialog: {
            get() {
                return this.screen_data.deleteConfirmDialog;
            },
            set(value) {
                this.screen_data.deleteConfirmDialog = value;
            }
        },
        instanceToDelete() {
            return this.screen_data.instanceToDelete;
        },
        selectedInstances: {
            get() {
                return this.screen_data.selectedInstances;
            },
            set(value) {
                this.screen_data.selectedInstances = value;
            }
        },
        hasSelectedInstances() {
            return this.screen_data.selectedInstances && this.screen_data.selectedInstances.length > 0;
        },
        selectedCount() {
            return this.screen_data.selectedInstances ? this.screen_data.selectedInstances.length : 0;
        },
        allVisibleSelected() {
            if (this.instances.length === 0) return false;
            return this.instances.every(item => this.screen_data.selectedInstances.includes(item.__row_id__));
        },
        someVisibleSelected() {
            return this.instances.some(item => this.screen_data.selectedInstances.includes(item.__row_id__));
        }
    },
    async mounted() {
        // Only initialize if not already loaded (preserves state on navigation back)
        if (!this.screen_data.initialLoadDone) {
            await this.component.initialize(parseInt(this.companydb_id), this.entity_type_uuid);
        } else {
            // Update company_id in case it changed
            this.component.setCompanyId(parseInt(this.companydb_id));
            // Reload instances while preserving filters and pagination
            this.loadInstances();
        }
    },
    watch: {
        entity_type_uuid(newVal) {
            // Switch to different component when entity type changes
            const component = document_components_factory.get_or_create_entity_instances_view_component(newVal);
            this.component = component;
            this.screen_data = component.screen_data;
            if (!this.screen_data.initialLoadDone) {
                this.component.initialize(parseInt(this.companydb_id), newVal);
            }
        },
        companydb_id(newVal) {
            const newId = parseInt(newVal);
            if (this.component.company_id !== newId) {
                this.component.setCompanyId(newId);
                this.component.loadInstances();
            }
        }
    },
    methods: {
        loadInstances() {
            this.component.loadInstances();
        },
        openCreateDialog() {
            if (this.useNewPageMode) {
                this.$router.push({
                    name: 'entity-instance-editor',
                    params: {
                        companydb_id: this.companydb_id,
                        entity_type_id: this.entity_type_uuid
                    }
                });
                return;
            }
            this.component.openCreateDialog();
        },
        openEditDialog(item) {
            if (this.useNewPageMode) {
                this.$router.push({
                    name: 'entity-instance-editor',
                    params: {
                        companydb_id: this.companydb_id,
                        entity_instance_id: item.__uuid__
                    }
                });
                return;
            }
            this.component.openEditDialog(item);
        },
        closeDialog() {
            this.component.closeDialog();
        },
        saveInstance() {
            this.component.saveInstance();
        },
        saveInstanceWithoutClosing() {
            this.component.saveInstanceWithoutClosing();
        },
        showSuccess(text) {
            this.component.showSuccess(text);
        },
        showError(text) {
            this.component.showError(text);
        },
        handleFilterAction(action_code) {
            this.component.handleFilterAction(action_code);
        },
        handleEditorAction(action_code) {
            if (action_code === 'save') {
                this.saveInstance();
            } else if (action_code === 'auto_save') {
                this.saveInstanceWithoutClosing();
            } else if (action_code === 'close') {
                this.closeDialog();
            }
        },
        handleClickOutside() {
            if (!this.hideDefaultClose) {
                this.closeDialog();
            }
        },
        onTableOptionsUpdate(newOptions) {
            const oldPage = this.screen_data.tableOptions.page;
            const oldItemsPerPage = this.screen_data.tableOptions.itemsPerPage;
            const oldSortBy = this.screen_data.tableOptions.sortBy;
            const oldSortDesc = this.screen_data.tableOptions.sortDesc;

            this.screen_data.tableOptions = newOptions;

            // Reload if pagination or sorting changed
            if (this.screen_data.initialLoadDone) {
                const paginationChanged = oldPage !== newOptions.page || oldItemsPerPage !== newOptions.itemsPerPage;
                const sortChanged = JSON.stringify(oldSortBy) !== JSON.stringify(newOptions.sortBy) ||
                                    JSON.stringify(oldSortDesc) !== JSON.stringify(newOptions.sortDesc);
                if (paginationChanged || sortChanged) {
                    this.screen_data.selectedInstances = [];
                    this.component.loadInstances();
                }
            }
        },
        getNestedFieldTitle(fieldPath) {
            if (!this.entityType || !this.entityType.schema || !this.entityType.schema.properties) {
                return fieldPath;
            }
            // Check for i18n translations first
            if (this.entityType.ui_config && this.entityType.ui_config.translations) {
                const locale = this.$i18n ? this.$i18n.locale : 'es';
                const translations = this.entityType.ui_config.translations;
                if (translations[locale] && translations[locale][fieldPath]) {
                    return translations[locale][fieldPath];
                }
            }
            // Fallback to schema title
            const parts = fieldPath.split('.');
            let current = this.entityType.schema;
            let title = fieldPath;
            for (let i = 0; i < parts.length; i++) {
                if (current.properties && current.properties[parts[i]]) {
                    current = current.properties[parts[i]];
                    title = current.title || parts[i];
                } else {
                    break;
                }
            }
            return title;
        },
        getNestedValue(item, fieldPath) {
            if (!item || !fieldPath) return '';
            const parts = fieldPath.split('.');
            let value = item;
            for (const part of parts) {
                if (value && typeof value === 'object' && part in value) {
                    value = value[part];
                } else {
                    return '';
                }
            }
            // Format value for display
            if (value === null || value === undefined) return '';
            if (typeof value === 'object') return JSON.stringify(value);
            // Apply value translations if available
            if (typeof value === 'string') {
                return this.translateValue(value);
            }
            return value;
        },
        translateValue(value) {
            if (!this.entityType || !this.entityType.ui_config || !this.entityType.ui_config.value_translations) {
                return value;
            }
            const locale = this.$i18n ? this.$i18n.locale : 'es';
            const translations = this.entityType.ui_config.value_translations;
            if (translations[locale] && translations[locale][value] !== undefined) {
                return translations[locale][value];
            }
            return value;
        },
        isSelected(item) {
            return this.screen_data.selectedInstances.includes(item.__row_id__);
        },
        toggleSelection(item) {
            const idx = this.screen_data.selectedInstances.indexOf(item.__row_id__);
            if (idx >= 0) {
                this.screen_data.selectedInstances.splice(idx, 1);
            } else {
                this.screen_data.selectedInstances.push(item.__row_id__);
            }
        },
        toggleSelectAll() {
            if (this.allVisibleSelected) {
                // Deselect all visible
                const visibleIds = new Set(this.instances.map(i => i.__row_id__));
                this.screen_data.selectedInstances = this.screen_data.selectedInstances.filter(
                    id => !visibleIds.has(id)
                );
            } else {
                // Select all visible (add any not already selected)
                const currentSet = new Set(this.screen_data.selectedInstances);
                this.instances.forEach(item => {
                    if (!currentSet.has(item.__row_id__)) {
                        this.screen_data.selectedInstances.push(item.__row_id__);
                    }
                });
            }
        },
        confirmDeleteSelected() {
            if (this.hasSelectedInstances) {
                this.screen_data.deleteConfirmDialog = true;
            }
        },
        cancelDelete() {
            this.screen_data.deleteConfirmDialog = false;
        },
        async executeDelete() {
            if (this.hasSelectedInstances) {
                const uuids = [...this.screen_data.selectedInstances];
                await this.component.deleteInstances(uuids);
                this.screen_data.deleteConfirmDialog = false;
            }
        },
        async executeListAction(action) {
            if (!this.hasSelectedInstances) return;
            this.screen_data.executingListAction = action.code;
            try {
                const selectedIds = new Set(this.screen_data.selectedInstances);
                const payloads = this.instances.filter(i => selectedIds.has(i.__row_id__));
                const result = await async_command_dispatcher.dispatch_with_result('execute.catalog.list.action', {
                    entity_type_id: this.entity_type_uuid,
                    payloads: payloads,
                    action_code: action.code,
                    company_id: parseInt(this.companydb_id)
                });
                const label = action.hover_text || action.description || action.code;
                if (result && result.errors && result.errors.length > 0) {
                    this.showError(`"${label}": ${result.processed_count} OK, ${result.errors.length} error(es)`);
                } else {
                    this.showSuccess(`"${label}" ejecutada en ${result.processed_count} registro(s)`);
                }
                this.screen_data.selectedInstances = [];
                this.loadInstances();
            } catch (error) {
                console.error('Error executing list action:', error);
                this.showError(`Error ejecutando acción: ${error.message || error}`);
            } finally {
                this.screen_data.executingListAction = false;
            }
        }
    }
};
</script>

<style scoped>
::v-deep .v-data-table-header > tr > th > span {
    color: var(--v-primary-base);
    font-size: 0.875rem;
}
::v-deep .v-data-table-header > tr > th {
    background-color: var(--v-primaryLight-base) !important;
}
::v-deep tbody > tr {
    color: #56565B;
}
::v-deep tbody tr:nth-of-type(even) {
    background-color: rgba(245, 245, 245, 0.47);
}
.clickable-rows >>> tbody tr,
.clickable-row {
    cursor: pointer;
}
.clickable-rows >>> tbody tr:hover,
.clickable-row:hover {
    background-color: #e3f2fd !important;
}
.dialog-card {
    display: flex;
    flex-direction: column;
    max-height: 90vh;
    overflow: hidden;
}
.dialog-header {
    flex-shrink: 0;
}
.dialog-content {
    flex: 1;
    overflow-y: auto;
    max-height: calc(90vh - 48px);
}
.filter-section {
    background-color: #f8f9fa;
    border-radius: 4px;
}
/* Sticky header table styles */
.sticky-header-table ::v-deep .v-data-table__wrapper {
    max-height: calc(100vh - 280px);
    overflow-y: auto;
}
.sticky-header-table ::v-deep thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    background-color: var(--v-primaryLight-base) !important;
}
</style>
